import { siteSchema } from '~/schemas'
import { fetchApi } from './helpers'

const booleanSetting = (value: unknown, fallback = true) => {
  if (value === undefined || value === null || value === '') return fallback
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0

  return !['0', 'false', 'no', 'off'].includes(String(value).toLowerCase())
}

/** Both /sites and /info carry the site; they must agree on Turnstile. */
export const turnstileSetting = (settings: Record<string, unknown> | undefined) => {
  const siteKey = settings?.turnstile_site_key
  if (!booleanSetting(settings?.turnstile_enabled, false) || typeof siteKey !== 'string' || !siteKey) {
    return null
  }

  return {
    siteKey,
    scope: settings?.turnstile_scope === 'all' ? 'all' as const : 'gift_cards' as const,
  }
}

export const fetchSiteData = async ({ siteId }: { siteId: string }) => {
  const response: any = await fetchApi(`/sites/${siteId}`)

  return siteSchema.parse({
    id: response.data?.id,
    name: response.data?.name,
    phoneNumber: response.settings?.data?.main_phone,
    emailAddress: response.settings?.data?.mail,
    address: response.settings?.data?.address,
    googleAnalyticsId: response.settings?.data?.ga_key,
    googleGtmId: response.settings?.data?.google_gtm_id,
    scriptSources: response.settings?.data?.script_sources,
    scriptBodies: response.settings?.data?.script_bodies,
    facebookAnalyticsId: response.settings?.data?.fb_key,
    facebookLink: response.settings?.data?.useful_link_facebook,
    instagramLink: response.settings?.data?.useful_link_instagram,
    tiktokLink: response.settings?.data?.useful_link_tiktok,
    youtubeLink: response.settings?.data?.useful_link_youtube,
    trustPilotLink: response.settings?.data?.useful_link_trustpilot,
    tripAdvisorLink: response.settings?.data?.useful_link_tripadvisor,
    ticketSupplierDescription: response.settings?.data?.ticket_supplier_description,
    hotelsEnabled: booleanSetting(response.settings?.data?.hotels_enabled, true),
    // The device graph is gated per brand, and the gate is read before a
    // visitor id is minted rather than after one is sent (BOAT-1895).
    deviceTrackingEnabled: booleanSetting(
      response.settings?.data?.device_tracking_enabled,
      true,
    ),
    dealsPageEnabled: booleanSetting(response.settings?.data?.deals_page_enabled, false),
    turnstile: turnstileSetting(response.settings?.data),
    currency: {
      code: response.currencies?.[0]?.data?.code,
      name: response.currencies?.[0]?.data?.name,
      roundUp: response.currencies?.[0]?.data?.round_up,
    },
    links: {
      terms: response.page_links?.terms,
    },
  })
}

export type SiteData = Awaited<ReturnType<typeof fetchSiteData>>
